import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, ShieldCheck, SearchCheck, FileCheck2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, setSEO } from "@/components/Reveal";
import { LogoMarquee } from "@/components/LogoMarquee";
import { ParallaxImage } from "@/components/ParallaxImage";
import { QUALITY_PROCESS, INFRASTRUCTURE, INDUSTRIES, IMAGES } from "@/lib/data";

const ISOBanner = () => (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="quality-iso-section">
        <div className="absolute inset-0 -z-10">
            <img src={IMAGES.components} alt="" className="w-full h-full object-cover opacity-[0.22]" />
            <div className="absolute inset-0 bg-sf-black/65" />
        </div>
        <div className="sf-container text-center">
            <Reveal>
                <BadgeCheck size={48} className="text-sf-green mx-auto" />
                <h2 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight text-white mt-8" data-testid="quality-iso-heading">
                    ISO 9001:2015
                </h2>
                <p className="font-mono text-xs tracking-[0.35em] uppercase text-sf-green mt-6">Certified Quality Management System</p>
                <p className="text-sf-silver text-base md:text-lg leading-relaxed mt-8 max-w-2xl mx-auto">
                    Certification is the floor, not the ceiling. Our quality management system governs every stage — from raw
                    material selection to final dispatch.
                </p>
            </Reveal>
        </div>
    </section>
);

const Motto = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="quality-motto">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <SectionHeading
                overline="Our Motto"
                title='"Success Through Quality"'
                description="It is printed on our walls and practiced on our floor. Every component, every batch, every customer — the same uncompromising standard."
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-px bg-white/10 border border-white/10">
                {[
                    { icon: ShieldCheck, label: "Dedicated Quality Management Team" },
                    { icon: SearchCheck, label: "Stage-wise Inspection & Testing" },
                    { icon: FileCheck2, label: "Documented Process Control" },
                ].map(({ icon: Icon, label }) => (
                    <div key={label} className="bg-sf-black p-8 flex items-center gap-5 hover:bg-sf-surface transition-colors duration-500" data-testid={`quality-point-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <Icon size={22} className="text-sf-green shrink-0" />
                        <p className="font-display font-bold text-white text-sm">{label}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const Process = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="quality-process">
        <div className="sf-container">
            <SectionHeading overline="How We Assure Quality" title="Four Gates. Zero Compromise." />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-16">
                {QUALITY_PROCESS.map((q, i) => (
                    <Reveal key={q.step} delay={i * 0.1} className="bg-sf-black">
                        <div className="p-10 h-full hover:bg-sf-surface transition-colors duration-500 group" data-testid={`quality-step-${q.step}`}>
                            <p className="font-display font-extrabold text-5xl text-white/10 group-hover:text-sf-green transition-colors duration-500">{q.step}</p>
                            <h3 className="font-display font-bold text-lg text-white mt-6">{q.title}</h3>
                            <p className="text-sf-silver text-sm leading-relaxed mt-3">{q.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const InfrastructureBlock = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="quality-infrastructure">
        <div className="sf-container">
            <SectionHeading
                overline="Our Infrastructure"
                title="Engineered for Scale"
                description="Sophisticated machinery, dedicated production lines, in-house R&D and a quality function embedded in every stage."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-16">
                {INFRASTRUCTURE.map((item, i) => (
                    <Reveal key={item.title} delay={(i % 2) * 0.1} className="bg-sf-black">
                        <div className="p-10 md:p-12 h-full hover:bg-sf-surface transition-colors duration-500 group" data-testid={`quality-infra-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <p className="font-mono text-sf-green text-sm">0{i + 1}</p>
                            <h3 className="font-display font-bold text-2xl text-white mt-6 group-hover:text-sf-green transition-colors duration-500">{item.title}</h3>
                            <p className="text-sf-silver text-sm leading-relaxed mt-4">{item.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
                <Reveal className="lg:col-span-7">
                    <ParallaxImage src={IMAGES.factory} alt="TIG welding of a bent tube" testid="quality-infra-gallery-main" className="h-[460px]">
                        <div className="absolute bottom-0 left-0 bg-sf-green text-sf-black font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 z-10">Tube Fabrication</div>
                    </ParallaxImage>
                </Reveal>
                <div className="lg:col-span-5 grid grid-rows-2 gap-6">
                    <Reveal delay={0.1}>
                        <div className="sf-image-frame group h-full" data-testid="quality-infra-gallery-bending">
                            <img src={IMAGES.machinery} alt="CNC tube bending machine" className="w-full h-full min-h-[200px] object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute bottom-0 left-0 bg-sf-black/80 backdrop-blur px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sf-green border-t border-r border-white/10">Tube Bending</div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="sf-image-frame group h-full" data-testid="quality-infra-gallery-finished">
                            <img src={IMAGES.components} alt="Finished chrome handlebars" className="w-full h-full min-h-[200px] object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute bottom-0 left-0 bg-sf-black/80 backdrop-blur px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sf-green border-t border-r border-white/10">Finished Components</div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

const IndustriesBlock = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="quality-industries">
        <div className="sf-container">
            <SectionHeading
                overline="Industries We Serve"
                title="Proven Where It Matters Most"
                description="Demanding applications, unforgiving tolerances — the environments our components are built for."
            />
            <div className="mt-16 border-t border-white/10">
                {INDUSTRIES.map((ind, i) => (
                    <Reveal key={ind.num} delay={i * 0.05}>
                        <div className="group grid grid-cols-12 gap-4 items-baseline py-8 border-b border-white/10 px-2 md:px-6 hover:bg-sf-surface transition-colors duration-500" data-testid={`quality-industry-${ind.num}`}>
                            <span className="col-span-2 md:col-span-1 font-mono text-sf-green text-sm">{ind.num}</span>
                            <h3 className="col-span-10 md:col-span-5 font-display font-bold text-xl md:text-2xl text-white group-hover:text-sf-green transition-colors duration-500">{ind.title}</h3>
                            <p className="col-span-10 col-start-3 md:col-span-5 md:col-start-8 text-sf-silver text-sm leading-relaxed">{ind.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.1}>
                <p className="sf-overline mt-24">Our Customers</p>
                <h3 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-white mt-5 max-w-2xl">
                    Our Industries & Clients
                </h3>
            </Reveal>
            <div className="mt-12">
                <LogoMarquee />
            </div>
        </div>
    </section>
);

const QualityCTA = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="quality-cta">
        <div className="sf-container flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <Reveal>
                <p className="sf-overline">Audit Us</p>
                <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white mt-5 max-w-2xl">
                    Quality You Can <span className="text-sf-green">Verify.</span>
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <Link to="/contact" data-testid="quality-cta-link" className="sf-btn-primary shrink-0">
                    Request a Facility Audit <ArrowUpRight size={16} />
                </Link>
            </Reveal>
        </div>
    </section>
);

export default function Quality() {
    useEffect(() => {
        setSEO("Quality & Certifications — Super Finishers", "ISO 9001:2015 certified quality management. Success Through Quality — inspection and process control at every manufacturing stage.");
    }, []);

    return (
        <>
            <PageHero
                overline="Quality, Infrastructure & Customers"
                titleLines={["Quality Is Not", "Inspected. It's Built."]}
                description="An ISO 9001:2015 certified quality management system, sophisticated infrastructure and the industries that trust us — all in one place."
                image="components"
            />
            <ISOBanner />
            <Motto />
            <Process />
            <InfrastructureBlock />
            <IndustriesBlock />
            <QualityCTA />
        </>
    );
}
